(() => {
  const root = document.getElementById('app');
  if (!root || typeof Vue === 'undefined' || typeof fabric === 'undefined') {
    return;
  }

  const query = Object.fromEntries(new URLSearchParams(window.location.search).entries());
  const apiBase = (root.dataset.apiBase || '').replace(/\/$/, '');
  const storeKey = query.store_key || root.dataset.storeKey || '';
  const token = query.token || '';
  const previewUpsell = query.preview === 'upsell';
  const previewDigital = query.preview === 'digital';
  const EXTRA_DISCOUNT = 0.15;

  let extraCatalog = { money_format: '${{amount}}', sizes: {}, stands: {}, preview_images: [] };
  try {
    extraCatalog = {
      ...extraCatalog,
      ...JSON.parse(document.getElementById('afp-extra-print-catalog')?.textContent || '{}'),
    };
  } catch (error) {
    console.warn('Extra print catalog could not be parsed', error);
  }

  const formatMoney = (cents, format) => {
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const tokenName = ((format || '').match(placeholder) || [])[1] || 'amount';
    const withDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const parts = fixed.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${dollars}${decimal}${parts[1]}` : dollars;
    };
    let value = withDelimiters(cents, 2, ',', '.');
    if (tokenName === 'amount_no_decimals') value = withDelimiters(cents, 0, ',', '.');
    if (tokenName === 'amount_with_comma_separator') value = withDelimiters(cents, 2, '.', ',');
    if (tokenName === 'amount_no_decimals_with_comma_separator') value = withDelimiters(cents, 0, '.', ',');
    if (tokenName === 'amount_with_apostrophe_separator') value = withDelimiters(cents, 2, "'", '.');
    if (tokenName === 'amount_no_decimals_with_space_separator') value = withDelimiters(cents, 0, ' ', '.');
    if (tokenName === 'amount_with_space_separator') value = withDelimiters(cents, 2, ' ', ',');
    if (tokenName === 'amount_with_period_and_space_separator') value = withDelimiters(cents, 2, ' ', '.');
    return (format || '${{amount}}').replace(placeholder, value);
  };

  const normaliseSize = (value) => {
    const text = String(value || '').toLowerCase().replace(/\s+/g, '');
    if (text.includes('a5')) return 'a5';
    if (text.includes('a4')) return 'a4';
    if (text.includes('magnet')) return 'magnet';
    if (text.includes('block')) return 'block';
    return text || 'a5';
  };

  const normaliseStand = (value) => {
    const text = String(value || '').toLowerCase();
    if (text.includes('wood')) return 'wood';
    if (text.includes('clear')) return 'clear';
    if (text.includes('black')) return 'black';
    return 'none';
  };

  const readJson = (selector) => {
    try {
      return JSON.parse(root.querySelector(selector)?.textContent || '{}');
    } catch (error) {
      console.warn(`${selector} could not be parsed`, error);
      return {};
    }
  };

  const mockupRenders = readJson('[data-mockup-renders]');
  const design = readJson('[data-design-schema]');
  const elements = Array.isArray(design.elements) ? design.elements : [];
  const fonts = design.fonts?.length ? design.fonts : ['Roboto'];
  const colors = design.colors?.length ? design.colors : ['#ffffff'];
  const dateFormats = design.date_formats?.length ? design.date_formats : ['DD/MM/YYYY'];

  const CANVAS_WIDTH = 1000;
  const CANVAS_HEIGHT = 1400;

  const withKey = (path) => {
    const url = new URL(apiBase + path);
    if (storeKey) {
      url.searchParams.set('store_key', storeKey);
    }
    return url.toString();
  };

  const SIZE_LABELS = {
    magnet: 'Magnet · 3.93" × 2.95" × 0.11" | 10cm × 7.5cm × 0.3cm',
    a5: 'A5 · 8.27" × 5.83" × 0.11" | 21cm × 14.8cm × 0.3cm',
    a4: 'A4 · 11.69" × 8.27" × 0.11" | 29.7cm × 21cm × 0.3cm',
    block: 'Block · 5.91" × 3.94" × 0.98" | 15cm × 10cm × 2.5cm',
  };

  const SIZE_NAMES = {
    magnet: 'Magnet',
    a5: 'A5',
    a4: 'A4',
    block: 'Block',
  };

  const SIZE_SHORT = {
    magnet: 'Magnet · 3.93" × 2.95" × 0.11"',
    a5: 'A5 · 8.27" × 5.83" × 0.11"',
    a4: 'A4 · 11.69" × 8.27" × 0.11"',
    block: 'Block · 5.91" × 3.94" × 0.98"',
  };

  const STAND_LABELS = {
    wood: 'With wood stand',
    clear: 'With clear stand',
    black: 'With black stand',
  };

  const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];

  const formatDate = (value, format) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
    if (!match) {
      return '';
    }
    const [, year, month, day] = match;
    const tokens = {
      YYYY: year,
      MMMM: MONTHS[Number(month) - 1],
      MM: month,
      DD: day,
      D: String(Number(day)),
    };
    return format.replace(/YYYY|MMMM|MM|DD|D/g, (part) => tokens[part]);
  };

  const blobToDataUrl = (blob) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(new Error('Could not read that portrait.'));
      reader.readAsDataURL(blob);
    });

  const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // On-screen preview matches the 1000px canvas. The 2100px bitmap is only built when saving.
  const PREVIEW_WIDTH = CANVAS_WIDTH;
  const EXPORT_WIDTH = 2100;
  const TEXT_RENDER_DELAY = 80;
  const TRAY_SLIDE_MS = 280;
  const THUMB_WIDTH = 320;
  // Signed image links expire about 60 minutes after the order is fetched.
  const LINK_MAX_AGE_MS = 50 * 60 * 1000;
  const THUMB_REFETCH_GAP_MS = 60 * 1000;

  const sortImages = (images) =>
    [...(images || [])].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.id - b.id);

  const MIN_REGEN_PHOTOS = 4;
  const MAX_REGEN_PHOTOS = 10;
  const MAX_REGEN_BYTES = 10 * 1024 * 1024;
  let regenLocalId = 1;

  const isAllowedRegenFile = (file) => {
    const type = (file?.type || '').toLowerCase();
    const name = (file?.name || '').toLowerCase();
    return type === 'image/jpeg' || type === 'image/png' || /\.(jpe?g|png)$/.test(name);
  };

  const ACCESSORY_NOTE = 'Clearer photos without hats or sunglasses give a stronger portrait.';
  const EXTRA_PERSON_COPY = 'Upload a photo of one person.';
  const ONE_PERSON_MESSAGE = /only the person|person or pet|extra people|multiple people|another person|other people|other person|more than one|second (?:person|subject)/i;
  const GROUP_MESSAGE = /\bgroup\b/i;

  const isGroupMessage = (message) => GROUP_MESSAGE.test(String(message || ''));
  const isOnePersonMessage = (message) => {
    const text = String(message || '');
    if (!text || isGroupMessage(text)) return false;
    return ONE_PERSON_MESSAGE.test(text);
  };
  const customerPhotoMessage = (message) => {
    const text = String(message || '').trim();
    if (!text || isGroupMessage(text)) return text;
    return isOnePersonMessage(text) ? EXTRA_PERSON_COPY : text;
  };
  const OTHER_PHOTO_ISSUE = /\b(blur(?:ry|red)?|extra people|multiple people|other people|another person|group photo|low quality|poor lighting|black and white|black & white|sepia|filters?|no face|not a face|face not visible|cropped|extreme angle|too dark|overexposed)\b/i;

  const photoQuality = (photo) => {
    const verdict = String(photo?.verdict || '').toLowerCase();
    const status = String(photo?.status || '').toLowerCase();
    if (verdict === 'poor' || verdict === 'rejected' || status === 'rejected') return 'rejected';
    if (verdict === 'warning' || status === 'warning') return 'warning';
    if (verdict === 'good' || status === 'accepted') return 'accepted';
    return '';
  };

  const isAccessoryOnly = (photo) => {
    const message = String(photo?.message || '').trim();
    if (!message) return false;
    const mentionsHatOrGlasses = /\b(sun\s*glasses?|hats?|large glasses)\b/i.test(message) || /glasses\s+covering/i.test(message);
    const coveringFace = /\b(hat|sunglasses|glasses)\b/i.test(message) && /covering the face/i.test(message);
    if (!mentionsHatOrGlasses && !coveringFace) return false;
    return !OTHER_PHOTO_ISSUE.test(message);
  };

  const isHardReject = (photo) => photoQuality(photo) === 'rejected';

  const isAcceptedRegenPhoto = (photo) => {
    const quality = photoQuality(photo);
    return quality === 'accepted' || quality === 'warning';
  };

  const unwrapRegenPayload = (data) => {
    if (!data || typeof data !== 'object') return data;
    if (
      data.id ||
      data.photo ||
      data.ready_for_checkout !== undefined ||
      data.blocking_reason !== undefined ||
      Array.isArray(data.photos)
    ) {
      return data;
    }
    if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
      return unwrapRegenPayload(data.data);
    }
    return data;
  };

  const sameRegenPhotoId = (left, right) => left != null && right != null && String(left) === String(right);

  const copyRegenPhotoFields = (photo, remote) => {
    if (!remote || typeof remote !== 'object') return;
    if (remote.id != null) photo.id = remote.id;
    if (remote.verdict != null) photo.verdict = remote.verdict || '';
    if (remote.status != null) photo.status = remote.status || '';
    if (remote.message != null) photo.message = remote.message || '';
    if ('role' in remote) photo.role = remote.role || '';
  };

  const isUsablePhoto = (photo) => {
    const quality = photoQuality(photo);
    return quality === 'accepted' || quality === 'warning';
  };

  const isUsableFace = (photo) => /^face/i.test(photo.role || '') && isUsablePhoto(photo);

  const isUsableFullBody = (photo) => /^full[_-]?body$/i.test(photo.role || '') && isUsablePhoto(photo);

  const digitalText = (value) => String(value || '').toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ').trim();

  const isDigitalItem = (item) => {
    if (!item) return false;
    const size = digitalText(item.size).replace(/ /g, '-');
    if (size === 'digital' || size === 'digital-only') return true;
    if (digitalText(item.title).includes('digital')) return true;
    const handle = digitalText(item.product_handle || item.handle || item.product?.handle);
    if (handle === 'digital' || handle === 'digital only' || handle.includes('digital only')) return true;
    const signals = [
      item.fulfilment,
      item.fulfillment,
      item.package,
      item.package_name,
      item.product_type,
      item.productType,
      item.properties?._fulfilment,
      item.properties?.Package,
      item.properties?.package,
    ];
    return signals.some((value) => {
      const text = digitalText(value);
      return text === 'digital only' || text.includes('digital only');
    });
  };

  const feedbackIconSvg = (code) => {
    const key = String(code || '').toLowerCase();
    const note = '<svg viewBox="0 0 16 16" fill="none"><circle cx="8" cy="8" r="5" stroke="currentColor" stroke-width="1.4"/><path d="M8 7.2V11M8 5.2h.01" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
    const sunglasses = '<svg viewBox="0 0 16 16" fill="none"><circle cx="4.6" cy="9" r="2.1" stroke="currentColor" stroke-width="1.4"/><circle cx="11.4" cy="9" r="2.1" stroke="currentColor" stroke-width="1.4"/><path d="M6.7 9h2.6" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
    const blur = '<svg viewBox="0 0 16 16" fill="none"><path d="M8 2.5v2.2M8 11.3v2.2M2.5 8h2.2M11.3 8h2.2" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
    const people = '<svg viewBox="0 0 16 16" fill="none"><circle cx="6" cy="5.2" r="1.6" stroke="currentColor" stroke-width="1.4"/><circle cx="11" cy="6" r="1.3" stroke="currentColor" stroke-width="1.4"/><path d="M2.8 12.2c.5-1.8 1.8-2.7 3.2-2.7s2.7.9 3.2 2.7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/></svg>';
    if (/sun|glass|hat|crop|angle/.test(key)) return sunglasses;
    if (/blur|quality|light|filter/.test(key)) return blur;
    if (/people|person|outfit|body|face/.test(key)) return people;
    return note;
  };

  // S3 only sends CORS headers when asked, so a copy cached by a plain request can block fetch().
  const fetchImage = async (url) => {
    let response;
    try {
      response = await fetch(url);
    } catch (error) {
      response = await fetch(url, { cache: 'reload' });
    }
    if (!response.ok) {
      throw new Error('Could not load that portrait.');
    }
    return response.blob();
  };

  const inflightFetches = new Map();
  const fetchImageShared = (url) => {
    if (!inflightFetches.has(url)) {
      const pending = fetchImage(url).finally(() => inflightFetches.delete(url));
      inflightFetches.set(url, pending);
    }
    return inflightFetches.get(url);
  };

  const jpegUrlFromCanvas = (source, widthLimit) => new Promise((resolve) => {
    const scale = Math.min(1, widthLimit / source.width);
    const width = Math.max(1, Math.round(source.width * scale));
    const height = Math.max(1, Math.round(source.height * scale));
    const element = document.createElement('canvas');
    element.width = width;
    element.height = height;
    element.getContext('2d').drawImage(source, 0, 0, width, height);
    element.toBlob((blob) => {
      resolve(blob ? URL.createObjectURL(blob) : '');
    }, 'image/jpeg', 0.82);
  });

  const downloadBlobCache = new Map();

  const cachedFetchImage = (url) => {
    if (!downloadBlobCache.has(url)) {
      const pending = fetchImage(url).catch((error) => {
        downloadBlobCache.delete(url);
        throw error;
      });
      downloadBlobCache.set(url, pending);
    }
    return downloadBlobCache.get(url);
  };

  const isPhoneDevice = () => {
    const ua = navigator.userAgent || '';
    const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    return iOS || /Android/i.test(ua);
  };

  const imageMimeType = async (blob) => {
    const declared = (blob.type || '').split(';')[0].toLowerCase();
    if (declared === 'image/jpg') {
      return 'image/jpeg';
    }
    if (declared.startsWith('image/')) {
      return declared;
    }
    const bytes = new Uint8Array(await blob.slice(0, 12).arrayBuffer());
    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return 'image/jpeg';
    }
    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
      return 'image/png';
    }
    if (bytes[0] === 0x47 && bytes[1] === 0x49 && bytes[2] === 0x46) {
      return 'image/gif';
    }
    if (
      bytes[0] === 0x52 && bytes[1] === 0x49 && bytes[2] === 0x46 && bytes[3] === 0x46
      && bytes[8] === 0x57 && bytes[9] === 0x45 && bytes[10] === 0x42 && bytes[11] === 0x50
    ) {
      return 'image/webp';
    }
    return 'image/jpeg';
  };

  const extensionForType = (type) => {
    if (type === 'image/png') return 'png';
    if (type === 'image/webp') return 'webp';
    if (type === 'image/gif') return 'gif';
    return 'jpg';
  };

  const shareImageFiles = async (files) => {
    if (!files.length || typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') {
      return false;
    }
    try {
      if (!navigator.canShare({ files })) {
        return false;
      }
      await navigator.share({ files });
      return true;
    } catch (error) {
      return error?.name === 'AbortError';
    }
  };

  const imageFromDataUrl = (dataUrl) =>
    new Promise((resolve, reject) => {
      fabric.Image.fromURL(dataUrl, (portrait) => {
        if (portrait?.width) {
          resolve(portrait);
        } else {
          reject(new Error('Could not draw that portrait.'));
        }
      });
    });

  const canvasFromBlob = async (blob, widthLimit) => {
    if (typeof createImageBitmap !== 'function') {
      const image = await imageFromDataUrl(await blobToDataUrl(blob));
      const source = image.getElement();
      const element = document.createElement('canvas');
      element.width = source.width || image.width;
      element.height = source.height || image.height;
      element.getContext('2d').drawImage(source, 0, 0, element.width, element.height);
      return element;
    }
    let bitmap;
    try {
      bitmap = widthLimit
        ? await createImageBitmap(blob, { resizeWidth: widthLimit, resizeQuality: 'high' })
        : await createImageBitmap(blob);
    } catch (error) {
      bitmap = await createImageBitmap(blob);
    }
    const width = widthLimit ? Math.min(widthLimit, bitmap.width) : bitmap.width;
    const height = widthLimit ? Math.round((bitmap.height * width) / bitmap.width) : bitmap.height;
    const element = document.createElement('canvas');
    element.width = width;
    element.height = height;
    element.getContext('2d').drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();
    return element;
  };

  const overlayUrls = window.AFP_OVERLAYS || {};
  const overlayElements = new Map();
  const loadOverlayElement = (key) => {
    const url = overlayUrls[key];
    if (!url) {
      return Promise.reject(new Error('Calendar overlay is missing.'));
    }
    if (!overlayElements.has(url)) {
      overlayElements.set(url, (async () => {
        try {
          return await canvasFromBlob(await fetchImage(url));
        } catch (error) {
          overlayElements.delete(url);
          throw error;
        }
      })());
    }
    return overlayElements.get(url);
  };

  const coverImage = (image, target) => {
    const scale = Math.max(CANVAS_WIDTH / image.width, CANVAS_HEIGHT / image.height);
    image.set({
      originX: 'center',
      originY: 'center',
      left: CANVAS_WIDTH / 2,
      top: CANVAS_HEIGHT / 2,
      scaleX: scale,
      scaleY: scale,
    });
    if (image.canvas !== target) target.add(image);
  };

  const placeOverlay = (image, target, edgeToEdge) => {
    const inset = edgeToEdge ? 0 : 0.03;
    const overlayWidth = CANVAS_WIDTH * (1 - inset * 2);
    const overlayHeight = CANVAS_HEIGHT * (1 - inset * 2);
    image.set({
      originX: 'left',
      originY: 'top',
      left: CANVAS_WIDTH * inset,
      top: CANVAS_HEIGHT * inset,
      scaleX: overlayWidth / image.width,
      scaleY: overlayHeight / image.height,
    });
    if (image.canvas !== target) target.add(image);
  };

  const fontLoads = new Map();
  const ensureFont = (font) => {
    if (!document.fonts?.load) {
      return Promise.resolve();
    }
    if (!fontLoads.has(font)) {
      fontLoads.set(
        font,
        Promise.all([
          document.fonts.load(`bold 60px "${font}"`),
          document.fonts.load(`italic 60px "${font}"`),
          document.fonts.load(`60px "${font}"`),
        ]).catch(() => {}),
      );
    }
    return fontLoads.get(font);
  };

  // Fabric objects must stay outside Vue's reactive proxies.
  let canvas = null;
  let renderVersion = 0;
  let renderQueued = false;
  let renderMode = null;
  let textRenderTimer = 0;
  let trayTimer = 0;
  let previewPortrait = null;
  let previewOverlay = null;
  let placedPortraitId = null;
  let placedOverlayKey = null;
  const textObjects = [];
  const portraitElements = new Map();
  const portraitUrls = new Map();
  const previewImages = new Map();
  const thumbBlobs = new Map();
  const thumbJobs = new Map();
  let thumbGeneration = 0;
  let lastThumbRefetch = 0;
  const failedThumbIds = new Set();
  let thumbChain = Promise.resolve();
  let textVersion = 0;

  const isOpen = (item) => !item.run?.selected_image_id && item.run?.status !== 'failed';

  const previewOrder = (digital) => {
    const images = (extraCatalog.preview_images || []).slice(0, 5).map((url, index) => ({
      id: index + 1,
      url,
      thumb_url: url,
      sort_order: index,
    }));
    if (digital) {
      return {
        orderNumber: '1042',
        items: [
          {
            title: 'Digital only',
            size: 'digital',
            stand: 'none',
            fulfilment: 'DIGITAL_ONLY',
            package: 'Digital only',
            properties: { Package: 'Digital only', _fulfilment: 'DIGITAL_ONLY' },
            run: {
              gallery_token: 'preview-digital',
              status: 'complete',
              selected_image_id: null,
              images,
            },
          },
        ],
      };
    }
    return {
      orderNumber: '1042',
      items: [
        {
          title: 'A5 acrylic print',
          size: 'a5',
          stand: 'wood',
          run: {
            gallery_token: 'preview',
            status: 'complete',
            selected_image_id: images[0]?.id || 1,
            images,
          },
        },
      ],
    };
  };
  const previewState = previewDigital ? previewOrder(true) : (previewUpsell ? previewOrder(false) : null);

  Vue.createApp({
    data() {
      return {
        bootError: '',
        loading: !(previewUpsell || previewDigital),
        statusMessage: '',
        statusIsError: false,
        canvasLoader: false,
        portraitFailed: false,
        overlayFailed: false,
        saving: false,
        downloading: false,
        downloadProgress: '',
        photoSheet: [],
        pollTimer: null,
        loadedAt: 0,
        orderId: null,
        orderNumber: previewState?.orderNumber || '',
        allSelected: previewUpsell,
        items: previewState?.items || [],
        previewUpsell,
        previewDigital,
        extraHidden: false,
        extraShowAll: false,
        extraSizeKey: '',
        extraChoiceKey: '',
        extraBuying: false,
        extraNotice: '',
        extraError: '',
        activeIndex: 0,
        started: false,
        pollIntervalMs: 5000,
        drafts: {},
        errorFields: [],
        customiseOpen: false,
        trayAnimating: false,
        thumbSources: {},
        showFontList: false,
        openTip: '',
        refetchedForImages: false,
        fonts,
        colors,
        dateFormats,
        overlayThumbUrl: overlayUrls.edge || '',
        shouldPoll: false,
        regenOpen: false,
        regenStep: 'feedback',
        regenSessionId: '',
        regenExistingCount: 0,
        regenPriorIds: [],
        regenBlockingReason: '',
        regenReadyForCheckout: false,
        regenReplaceFlight: null,
        regenPhotos: [],
        regenError: '',
        regenNotice: '',
        regenBusy: false,
        regenReplaceIndex: -1,
        regenUploadQueue: null,
      };
    },
    computed: {
      activeItem() {
        return this.items[this.activeIndex] || null;
      },
      activeRun() {
        return this.activeItem?.run || null;
      },
      activeImages() {
        return sortImages(this.activeRun?.images);
      },
      saveToPhotos() {
        return isPhoneDevice();
      },
      draft() {
        const item = this.activeItem;
        if (!item?.run?.gallery_token) return null;
        return this.drafts[this.draftKey(item, this.activeIndex)] || null;
      },
      selectedImage() {
        return this.activeImages.find((image) => image.id === this.draft?.imageId) || null;
      },
      otherImages() {
        return this.activeImages.filter((image) => image.id !== this.draft?.imageId);
      },
      isWaiting() {
        const status = this.activeRun?.status;
        return Boolean(status) && !['complete', 'failed'].includes(status);
      },
      isFailed() {
        return this.activeRun?.status === 'failed';
      },
      isSaved() {
        return Boolean(this.activeRun?.selected_image_id);
      },
      canSkipForward() {
        return this.isSaved || this.isFailed || this.isWaiting;
      },
      anyInProgress() {
        return this.items.some((item) => {
          const status = item.run?.status;
          return status && !['complete', 'failed'].includes(status);
        });
      },
      hasRegeneratingTemplates() {
        return this.items.some((item) => (item.run?.regenerating_template_ids || []).length > 0);
      },
      regeneratingIds() {
        return (this.activeRun?.regenerating_template_ids || []).map(String);
      },
      failedIds() {
        return (this.activeRun?.failed_template_ids || []).map(String);
      },
      awaitingImages() {
        return Boolean(this.activeRun) && !this.activeImages.length && !this.isFailed;
      },
      regenRemaining() {
        const value = Number(this.activeRun?.regenerations_remaining);
        return Number.isFinite(value) ? value : 0;
      },
      regenRemainingLabel() {
        if (this.regenRemaining <= 0) return '';
        return this.regenRemaining === 1 ? '1 regeneration left' : `${this.regenRemaining} regenerations left`;
      },
      canRegenerate() {
        return this.regenRemaining > 0 && this.activeImages.length > 0;
      },
      showPaidRegenNote() {
        const raw = this.activeRun?.regenerations_remaining;
        if (raw == null || raw === '') return false;
        const remaining = Number(raw);
        return Number.isFinite(remaining) && remaining === 0;
      },
      supportHref() {
        const params = new URLSearchParams();
        const order = this.orderNumber || (this.orderId != null ? String(this.orderId) : '');
        if (order) params.set('order', order);
        params.set('topic', 'Prints');
        params.set(
          'message',
          "Hi, the portraits still aren't quite right. I'd like to organise a paid third regeneration for this order."
        );
        const base = window.AFP_CONTACT_URL || '/pages/contact';
        const qs = params.toString();
        return qs ? base + '?' + qs : base;
      },
      canUploadPhotos() {
        return this.canRegenerate && Boolean(this.activeRun?.upload_session_id);
      },
      referenceFeedback() {
        const value = this.activeRun?.reference_feedback;
        if (!Array.isArray(value)) return [];
        return value.filter((row) => row && String(row.label || '').trim());
      },
      feedbackState() {
        const value = this.activeRun?.reference_feedback;
        if (!Array.isArray(value)) return 'unknown';
        return this.referenceFeedback.length ? 'issues' : 'clear';
      },
      regenLocked() {
        return this.regenBusy;
      },
      showSceneGroups() {
        const counts = new Map();
        this.activeImages.forEach((image) => {
          if (image.generation_template_id == null) return;
          const key = String(image.generation_template_id);
          counts.set(key, (counts.get(key) || 0) + 1);
        });
        return [...counts.values()].some((count) => count > 1);
      },
      sceneGroups() {
        const order = [];
        const map = new Map();
        this.activeImages.forEach((image) => {
          const key = image.generation_template_id != null ? String(image.generation_template_id) : `solo-${image.id}`;
          if (!map.has(key)) {
            const group = { key, templateId: image.generation_template_id ?? null, images: [], busy: false, failed: false };
            map.set(key, group);
            order.push(group);
          }
          map.get(key).images.push(image);
        });
        order.forEach((group) => {
          if (group.templateId == null) return;
          const id = String(group.templateId);
          group.busy = this.regeneratingIds.includes(id);
          group.failed = this.failedIds.includes(id);
        });
        this.regeneratingIds.forEach((id) => {
          if (!map.has(id)) {
            order.push({ key: `pending-${id}`, templateId: id, images: [], busy: true, failed: false });
          }
        });
        return order;
      },
      flatSlots() {
        const slots = [];
        const seen = new Set();
        this.activeImages.forEach((image) => {
          slots.push({ key: `img-${image.id}`, image });
          const id = image.generation_template_id;
          if (id == null || seen.has(String(id)) || !this.isTemplateBusy(image)) return;
          seen.add(String(id));
          slots.push({ key: `pending-${id}`, image: null });
        });
        return slots;
      },
      regenUploadMax() {
        return MAX_REGEN_PHOTOS;
      },
      regenUploadLabel() {
        return `Upload ${MIN_REGEN_PHOTOS}–${MAX_REGEN_PHOTOS} photos`;
      },
      regenUploadProgress() {
        const photos = this.regenPhotos;
        const total = photos.length;
        const uploaded = photos.filter((photo) => photo.id).length;
        const activeIndex = photos.findIndex((photo) => photo.uploading);
        const uploading = activeIndex >= 0 || photos.some((photo) => photo.uploading);
        const current = activeIndex >= 0 ? activeIndex + 1 : Math.min(uploaded + 1, total);
        const percent = total ? Math.round((uploaded / total) * 100) : 0;
        return {
          show: uploading && total > 0,
          current,
          total,
          percent,
          fill: percent === 0 ? 18 : percent,
          label: `Uploading photo ${current} of ${total}`,
        };
      },
      regenPhotoMessage() {
        const photos = this.regenPhotos;
        if (photos.some((photo) => photo.uploading || photo.deleting)) return '';
        if (photos.length < MIN_REGEN_PHOTOS) return photos.length ? `${this.regenUploadLabel}.` : '';
        if (photos.some((photo) => !photo.id)) return '';
        if (photos.some(isHardReject)) {
          return 'Replace the photos marked below, then try again.';
        }
        if (this.regenReadyForCheckout && photos.every(isAcceptedRegenPhoto)) return '';
        if (this.regenBlockingReason) return customerPhotoMessage(this.regenBlockingReason);
        if (photos.filter(isUsableFace).length < 2) {
          return 'Please upload two clear photos of the face from different angles.';
        }
        if (!photos.some(isUsableFullBody)) return 'Please upload a full-body photo.';
        return '';
      },
      regenPhotoNote() {
        if (this.regenPhotoMessage) return '';
        const photos = this.regenPhotos;
        if (photos.some((photo) => photo.uploading || photo.deleting)) return '';
        if (photos.length < MIN_REGEN_PHOTOS || photos.some((photo) => !photo.id)) return '';
        if (!this.regenReadyForCheckout) return '';
        return photos.some(isAccessoryOnly) ? ACCESSORY_NOTE : '';
      },
      regenReady() {
        return !this.regenBusy
          && this.regenReadyForCheckout
          && !this.regenPhotoMessage
          && this.regenPhotos.length >= MIN_REGEN_PHOTOS
          && this.regenPhotos.every((photo) => photo.id && !photo.uploading && !photo.deleting && !isHardReject(photo));
      },
      fields() {
        return Object.fromEntries(elements.map((element) => [element.id, element]));
      },
      isDigital() {
        return isDigitalItem(this.activeItem);
      },
      allDigital() {
        return this.items.length > 0 && this.items.every((item) => isDigitalItem(item));
      },
      downloadHint() {
        if (this.saveToPhotos) return 'Adds them to your Photos app';
        return this.isDigital ? 'Every image includes your text' : 'Get all designs in high resolution';
      },
      loadingHint() {
        return this.isDigital ? 'Loading your images…' : 'Loading your prints…';
      },
      emptyOrderMessage() {
        return this.isDigital ? 'We couldn’t find any images on this order.' : 'We couldn’t find any prints on this order.';
      },
      failedTitle() {
        return this.isDigital ? 'We couldn’t create these images' : 'We couldn’t create this print';
      },
      calendarFullBleed() {
        return this.isDigital;
      },
      calendarOn() {
        return this.hasCalendar(this.selectedImage);
      },
      stepLabel() {
        const step = `${this.isDigital ? 'Design' : 'Print'} ${this.activeIndex + 1} of ${this.items.length}`;
        return !this.isDigital && this.sizeName ? `${step} · ${this.sizeName}` : step;
      },
      pageTitle() {
        if (this.activeItem?.title) return this.activeItem.title;
        return this.isDigital ? 'Customise your images' : 'Customise your firefighter print';
      },
      sizeKey() {
        if (this.isDigital) return 'digital';
        return String(this.activeItem?.size || 'a5').toLowerCase();
      },
      standKey() {
        const sizeMap = mockupRenders[this.sizeKey] || {};
        const stand = String(this.activeItem?.stand || 'none').toLowerCase();
        return sizeMap[stand] ? stand : 'none';
      },
      mockupPair() {
        if (this.isDigital) return null;
        const sizeMap = mockupRenders[this.sizeKey] || {};
        return sizeMap[this.standKey] || sizeMap.none || null;
      },
      backgroundStyle() {
        const url = this.mockupPair?.render;
        return url ? { backgroundImage: `url("${url}")` } : {};
      },
      maskStyle() {
        const url = this.mockupPair?.mask;
        return url ? { backgroundImage: `url("${url}")` } : {};
      },
      sizeName() {
        if (!this.activeItem) {
          return '';
        }
        if (this.isDigital) return 'Digital';
        return SIZE_NAMES[this.sizeKey] || String(this.activeItem.size || '').toUpperCase();
      },
      productBadge() {
        if (this.isDigital) return 'Digital only';
        if (this.sizeKey === 'magnet') {
          return 'Magnet';
        }
        if (this.sizeKey === 'block') {
          return 'Block';
        }
        return 'Acrylic print';
      },
      sizeLabel() {
        if (!this.activeItem || this.isDigital) {
          return '';
        }
        return SIZE_LABELS[this.sizeKey] || '';
      },
      sizeShort() {
        if (this.isDigital) return '';
        return SIZE_SHORT[this.sizeKey] || String(this.activeItem?.size || '');
      },
      standLabel() {
        if (this.isDigital) return '';
        return STAND_LABELS[String(this.activeItem?.stand || '').toLowerCase()] || '';
      },
      confirmLabel() {
        if (this.saving) {
          return 'Saving…';
        }
        if (this.isSaved) {
          return this.isDigital ? 'Update this design' : 'Update this print';
        }
        const remaining = this.items.filter(isOpen).length;
        if (this.isDigital) {
          return remaining > 1 ? 'Save and go to next' : 'Save';
        }
        return remaining > 1 ? 'Save and go to next print' : 'Save and send to production';
      },
      extraChoices() {
        const savedIds = new Set();
        this.items.forEach((item) => {
          if (isDigitalItem(item)) return;
          const selectedId = item.run?.selected_image_id;
          if (selectedId != null) savedIds.add(String(selectedId));
        });
        const choices = [];
        const seen = new Set();
        this.items.forEach((item) => {
          if (isDigitalItem(item)) return;
          sortImages(item.run?.images).forEach((image, index) => {
            const src = image?.thumb_url || image?.url || '';
            if (!src || image.id == null || savedIds.has(String(image.id))) return;
            const dedupe = `${item.run?.gallery_token || ''}:${image.id}`;
            if (seen.has(dedupe)) return;
            seen.add(dedupe);
            choices.push({
              key: dedupe,
              imageId: image.id,
              src,
              label: `Portrait ${index + 1}`,
              size: normaliseSize(item.size),
              stand: normaliseStand(item.stand),
              galleryToken: item.run?.gallery_token || '',
            });
          });
        });
        return choices;
      },
      visibleExtraChoices() {
        if (this.extraShowAll) return this.extraChoices;
        return this.extraChoices.slice(0, 4);
      },
      extraHasMore() {
        return this.extraChoices.length > 4;
      },
      extraChoice() {
        return this.extraChoices.find((choice) => choice.key === this.extraChoiceKey) || this.extraChoices[0] || null;
      },
      canOfferExtra() {
        return this.extraChoices.length > 0;
      },
      orderedSize() {
        const item = this.items.find((entry) => !isDigitalItem(entry));
        return normaliseSize(item?.size);
      },
      extraSizeOptions() {
        const order = ['magnet', 'a5', 'a4', 'block'];
        return order
          .filter((key) => extraCatalog.sizes?.[key])
          .map((key) => ({
            key,
            name: extraCatalog.sizes[key].name || SIZE_NAMES[key] || key.toUpperCase(),
          }));
      },
      selectedExtraSize() {
        const chosen = this.extraSizeKey || this.orderedSize || this.extraChoice?.size || 'a5';
        if (extraCatalog.sizes?.[chosen]) return chosen;
        return this.extraSizeOptions[0]?.key || chosen;
      },
      extraSize() {
        return extraCatalog.sizes?.[this.selectedExtraSize] || null;
      },
      extraStand() {
        if (!this.extraSize || this.extraSize.stands === false) return null;
        const stand = extraCatalog.stands?.[this.extraChoice?.stand];
        return stand?.id ? stand : null;
      },
      extraPrintPrice() {
        return Number(this.extraSize?.price) || 0;
      },
      extraSalePrice() {
        return Math.round(this.extraPrintPrice * (1 - EXTRA_DISCOUNT));
      },
      extraStandPrice() {
        return Number(this.extraStand?.price) || 0;
      },
      extraTotal() {
        return this.extraSalePrice + this.extraStandPrice;
      },
      canBuyExtra() {
        if (this.extraBuying || !this.extraChoice) return false;
        if (this.previewUpsell) return true;
        return Boolean(this.extraSize?.id);
      },
    },
    watch: {
      regenStep(step) {
        if (step === 'guide') {
          this.$nextTick(() => this.mountPhotoGuide());
        }
      },
    },
    created() {
      if (this.previewDigital) {
        this.items.forEach((item, index) => this.ensureDraft(item, index));
      }
    },
    mounted() {
      this.boundTipPointerDown = this.onTipPointerDown.bind(this);
      document.addEventListener('pointerdown', this.boundTipPointerDown);

      if (this.previewUpsell) {
        this.loading = false;
        return;
      }

      if (!this.previewDigital && (!apiBase || !storeKey || !token)) {
        this.bootError = 'This selection link is missing a store or order token.';
        this.loading = false;
        return;
      }

      canvas = new fabric.StaticCanvas('canvas', {
        width: CANVAS_WIDTH,
        height: CANVAS_HEIGHT,
        backgroundColor: '#ffffff',
        enableRetinaScaling: false,
        renderOnAddRemove: false,
      });

      if (this.previewDigital) {
        this.loading = false;
        this.scheduleRender();
        return;
      }

      this.loadOrder();
    },
    unmounted() {
      document.removeEventListener('pointerdown', this.boundTipPointerDown);
      clearTimeout(this.pollTimer);
      clearTimeout(textRenderTimer);
      clearTimeout(trayTimer);
      thumbGeneration += 1;
      thumbBlobs.forEach((entry) => URL.revokeObjectURL(entry.objectUrl));
      thumbBlobs.clear();
      this.regenPhotos.forEach((photo) => {
        if (photo.previewIsBlob && photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
      });
      document.body.style.overflow = '';
    },
    methods: {
      async jsonFetch(url, options = {}) {
        const response = await fetch(url, options);
        const data = await response.json().catch(() => ({}));
        if (!response.ok) {
          const error = new Error(data.message || `Request failed (${response.status})`);
          error.status = response.status;
          throw error;
        }
        return data;
      },
      lineImageId(item) {
        const candidates = [
          item?.image_id,
          item?.purchased_image_id,
          item?.properties?._image_id,
          item?.properties?.image_id,
        ];
        return candidates.find((value) => value != null && value !== '') ?? null;
      },
      draftKey(item, index) {
        const token = item?.run?.gallery_token || 'line';
        const lineId = item?.id ?? item?.line_id ?? item?.line_item_id;
        if (lineId != null && String(lineId) !== '') return `${token}:line:${lineId}`;
        const purchased = this.lineImageId(item);
        if (purchased != null) return `${token}:image:${purchased}`;
        const selected = item?.run?.selected_image_id;
        if (selected != null && String(selected) !== '') return `${token}:selected:${selected}`;
        return `${token}:index:${index}`;
      },
      imageIdIn(images, value) {
        if (value == null || value === '') return null;
        const found = images.find((image) => String(image.id) === String(value));
        return found ? found.id : null;
      },
      startingImageId(item, images) {
        return this.imageIdIn(images, item?.run?.selected_image_id)
          || this.imageIdIn(images, this.lineImageId(item))
          || images[0]?.id
          || null;
      },
      ensureDraft(item, index) {
        const run = item.run;
        if (!run?.gallery_token) {
          return;
        }
        const images = sortImages(run.images);
        const key = this.draftKey(item, index);
        const existing = this.drafts[key];
        if (existing) {
          if (!existing.calendarOverlays || typeof existing.calendarOverlays !== 'object') {
            existing.calendarOverlays = {};
          }
          if (!images.some((image) => image.id === existing.imageId)) {
            existing.imageId = this.startingImageId(item, images);
          }
          return;
        }

        const values = {};
        elements.forEach((element) => {
          if (element.editable && ['text', 'textarea', 'date', 'radio'].includes(element.editable_type)) {
            values[element.id] = element.initial ?? '';
          }
        });

        this.drafts[key] = {
          imageId: this.startingImageId(item, images),
          values,
          font: fonts.includes(design.default_font) ? design.default_font : fonts[0],
          color: colors.includes(design.default_color) ? design.default_color : colors[0],
          dateFormat: dateFormats[0],
          edgeToEdge: false,
          calendarOverlays: {},
        };
      },
      nextOpenIndex(from, fallback) {
        const count = this.items.length;
        for (let offset = 0; offset < count; offset += 1) {
          const index = (from + offset) % count;
          if (isOpen(this.items[index])) {
            return index;
          }
        }
        return Math.min(Math.max(fallback, 0), Math.max(count - 1, 0));
      },
      previewStamp() {
        const image = this.selectedImage;
        if (!image) {
          return `empty:${this.activeRun?.status || ''}:${this.activeIndex}`;
        }
        return `${this.activeIndex}:${image.id}:${image.url}`;
      },
      applyOrder(payload) {
        const previousStamp = this.previewStamp();
        const previousImages = new Map();
        const keepLinks = this.loadedAt && Date.now() - this.loadedAt < LINK_MAX_AGE_MS;
        if (keepLinks) {
          this.items.forEach((item) => {
            (item.run?.images || []).forEach((image) => {
              if (image?.id != null) previousImages.set(image.id, image);
            });
          });
        }
        this.loadedAt = Date.now();
        this.orderId = payload.order_id ?? null;
        this.orderNumber = payload.order_number || '';
        this.allSelected = Boolean(payload.all_selected);
        const items = Array.isArray(payload.items) ? payload.items : [];
        if (keepLinks) {
          items.forEach((item) => {
            if (!Array.isArray(item.run?.images)) return;
            item.run.images = item.run.images.map((image) => {
              const previous = previousImages.get(image.id);
              if (!previous?.url || failedThumbIds.has(image.id)) {
                if (image?.id != null) failedThumbIds.delete(image.id);
                return image;
              }
              return {
                ...image,
                url: previous.url,
                thumb_url: previous.thumb_url || image.thumb_url,
              };
            });
          });
        }
        this.items = items;
        const hasPoll = payload.poll_interval_ms != null && payload.poll_interval_ms !== '';
        this.shouldPoll = hasPoll;
        this.pollIntervalMs = hasPoll ? (Number(payload.poll_interval_ms) || 5000) : this.pollIntervalMs;
        this.items.forEach((item, index) => this.ensureDraft(item, index));

        if (!this.started && this.items.length) {
          this.activeIndex = this.nextOpenIndex(0, 0);
          this.started = true;
        }
        if (this.activeIndex >= this.items.length) {
          this.activeIndex = Math.max(this.items.length - 1, 0);
        }
        if (this.previewStamp() !== previousStamp) {
          this.scheduleRender();
        }
      },
      async loadOrder() {
        if (this.previewDigital || this.previewUpsell) return;
        clearTimeout(this.pollTimer);
        try {
          const payload = await this.jsonFetch(withKey('/api/generation-orders/' + token));
          this.applyOrder(payload);
          this.schedulePoll();
        } catch (error) {
          if (!this.items.length) {
            this.bootError = error.message;
          } else {
            this.statusMessage = error.message;
            this.statusIsError = true;
          }
        } finally {
          this.loading = false;
        }
      },
      async refreshIfStale() {
        if (this.loadedAt && Date.now() - this.loadedAt > LINK_MAX_AGE_MS) {
          await this.loadOrder();
        }
      },
      onThumbError(image) {
        if (image?.id != null) failedThumbIds.add(image.id);
        const now = Date.now();
        if (now - lastThumbRefetch < THUMB_REFETCH_GAP_MS) {
          return;
        }
        lastThumbRefetch = now;
        this.loadOrder();
      },
      goTo(index) {
        if (index < 0 || index >= this.items.length) {
          return;
        }
        this.activeIndex = index;
        this.closePhotoSheet();
        this.errorFields = [];
        this.statusMessage = '';
        this.statusIsError = false;
        this.showFontList = false;
        this.scheduleRender();
      },
      pickImage(image) {
        this.draft.imageId = image.id;
        this.scheduleRender();
      },
      toggleCustomise() {
        this.slideTray(!this.customiseOpen);
      },
      slideTray(open) {
        if (open === this.customiseOpen) return;
        if (!open) this.showFontList = false;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          this.trayAnimating = false;
          this.customiseOpen = open;
          return;
        }
        clearTimeout(trayTimer);
        if (this.trayAnimating) {
          this.customiseOpen = open;
          trayTimer = setTimeout(() => {
            this.trayAnimating = false;
          }, TRAY_SLIDE_MS + 50);
          return;
        }
        this.trayAnimating = true;
        this.$nextTick(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              this.customiseOpen = open;
              trayTimer = setTimeout(() => {
                this.trayAnimating = false;
              }, TRAY_SLIDE_MS + 50);
            });
          });
        });
      },
      finishTraySlide(event) {
        if (event.propertyName !== 'grid-template-rows') return;
        if (event.target !== this.$refs.customTray) return;
        clearTimeout(trayTimer);
        this.trayAnimating = false;
      },
      onFieldInput(id) {
        const hadErrors = this.errorFields.length > 0;
        this.errorFields = this.errorFields.filter((field) => field !== id);
        if (hadErrors && !this.errorFields.length && this.statusIsError) {
          this.statusMessage = '';
          this.statusIsError = false;
        }
        this.scheduleRender(true);
      },
      changeFont(font) {
        this.draft.font = font;
        this.showFontList = false;
        this.scheduleRender(true);
      },
      changeColor(color) {
        this.draft.color = color;
        this.scheduleRender(true);
      },
      toggleTip(id) {
        if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
        this.openTip = this.openTip === id ? '' : id;
      },
      onTipPointerDown(event) {
        if (!this.openTip) return;
        const target = event.target;
        if (target instanceof Element && target.closest('.update-tip')) return;
        this.openTip = '';
      },
      portraitCaption(image) {
        const number = this.portraitNumber(image);
        if (this.draft?.imageId === image?.id) {
          return this.isDigital ? 'Selected' : 'On your print';
        }
        return `Portrait ${number}`;
      },
      portraitPickLabel(image) {
        const number = this.portraitNumber(image);
        return this.isDigital ? `Select portrait ${number}` : `Use portrait ${number} on your print`;
      },
      navLabel(direction) {
        const noun = this.isDigital ? 'design' : 'print';
        return direction === 'previous' ? `Previous ${noun}` : `Next ${noun}`;
      },
      hasCalendar(image) {
        if (!image || !this.draft?.calendarOverlays) return false;
        return Boolean(this.draft.calendarOverlays[image.id]);
      },
      setEdge(checked) {
        this.draft.edgeToEdge = checked;
      },
      setCalendar(checked) {
        const id = this.draft?.imageId;
        if (id == null || !this.draft?.calendarOverlays) return;
        this.draft.calendarOverlays[id] = checked;
        this.scheduleRender();
      },
      scheduleRender(textOnly = false) {
        if (textOnly) {
          if (renderMode === 'full') return;
          renderMode = 'text';
          clearTimeout(textRenderTimer);
          textRenderTimer = setTimeout(() => {
            if (renderMode === 'text') renderMode = null;
            this.renderDesign({ textOnly: true });
          }, TEXT_RENDER_DELAY);
          return;
        }
        clearTimeout(textRenderTimer);
        renderMode = 'full';
        if (renderQueued) return;
        renderQueued = true;
        setTimeout(() => {
          renderQueued = false;
          renderMode = null;
          this.renderDesign({ textOnly: false });
        }, 0);
      },
      portraitElement(image, width) {
        const known = portraitUrls.get(image.id);
        if (known && known !== image.url) {
          portraitElements.delete(`${image.id}:${PREVIEW_WIDTH}`);
          portraitElements.delete(`${image.id}:${EXPORT_WIDTH}`);
          previewImages.delete(image.id);
          if (canvas && previewPortrait && placedPortraitId === image.id) {
            canvas.remove(previewPortrait);
            previewPortrait = null;
            placedPortraitId = null;
          }
        }
        portraitUrls.set(image.id, image.url);
        const key = `${image.id}:${width}`;
        if (!portraitElements.has(key)) {
          const pending = (async () => canvasFromBlob(await fetchImageShared(image.url), width))();
          portraitElements.set(key, pending);
          pending.catch(() => portraitElements.delete(key));
        }
        return portraitElements.get(key);
      },
      async previewImageFor(image) {
        await this.portraitElement(image, PREVIEW_WIDTH);
        if (!previewImages.has(image.id)) {
          const element = await this.portraitElement(image, PREVIEW_WIDTH);
          previewImages.set(image.id, new fabric.Image(element, { objectCaching: true }));
        }
        return previewImages.get(image.id);
      },
      thumbSrc(image) {
        if (!image) return '';
        const objectUrl = this.thumbSources[image.id] || '';
        const cached = thumbBlobs.get(image.id);
        if (objectUrl && cached) return objectUrl;
        if (image.thumb_url) return image.thumb_url;
        return '';
      },
      async buildThumb(image) {
        if (!image?.url || image.thumb_url) return;
        const cached = thumbBlobs.get(image.id);
        if (cached?.source === image.url) return;
        let source = null;
        const preview = previewImages.get(image.id);
        if (preview && portraitUrls.get(image.id) === image.url) {
          source = preview.getElement();
        }
        const bitmap = source || await canvasFromBlob(await fetchImageShared(image.url), THUMB_WIDTH);
        const objectUrl = await jpegUrlFromCanvas(bitmap, THUMB_WIDTH);
        if (!objectUrl) return;
        if (!this.activeImages.some((item) => item.id === image.id && item.url === image.url)) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        if (cached?.objectUrl) URL.revokeObjectURL(cached.objectUrl);
        thumbBlobs.set(image.id, { source: image.url, objectUrl });
        this.thumbSources = { ...this.thumbSources, [image.id]: objectUrl };
      },
      ensureThumb(image) {
        if (!image?.url || image.thumb_url) return Promise.resolve();
        const key = `${image.id}:${image.url}`;
        if (thumbBlobs.get(image.id)?.source === image.url) return Promise.resolve();
        if (!thumbJobs.has(key)) {
          const job = this.buildThumb(image).finally(() => thumbJobs.delete(key));
          thumbJobs.set(key, job);
        }
        return thumbJobs.get(key);
      },
      queueThumbs() {
        const images = this.activeImages.filter((image) => image?.url && !image.thumb_url);
        const missing = images.filter((image) => thumbBlobs.get(image.id)?.source !== image.url);
        if (!missing.length) return;
        if (missing.every((image) => thumbJobs.has(`${image.id}:${image.url}`))) return;
        const generation = ++thumbGeneration;
        const selected = this.selectedImage;
        thumbChain = thumbChain.then(async () => {
          if (generation !== thumbGeneration) return;
          if (selected && placedPortraitId === selected.id) {
            try {
              await this.ensureThumb(selected);
            } catch (error) {
              // The skeleton stays until a later pass.
            }
          }
          for (const image of images) {
            if (generation !== thumbGeneration) return;
            if (!image || image.thumb_url || image.id === selected?.id) continue;
            try {
              await this.ensureThumb(image);
            } catch (error) {
              // Leave this thumbnail as a skeleton.
            }
          }
        }).catch(() => {});
      },
      clearPreview() {
        if (!canvas) return;
        if (previewPortrait) canvas.remove(previewPortrait);
        if (previewOverlay) canvas.remove(previewOverlay);
        textObjects.forEach((object) => canvas.remove(object));
        textObjects.length = 0;
        previewPortrait = null;
        previewOverlay = null;
        placedPortraitId = null;
        placedOverlayKey = null;
        canvas.backgroundColor = '#ffffff';
      },
      addText(target) {
        const draft = this.draft;
        if (!draft) return;
        elements.forEach((element) => {
          if (element.toggle_field && draft.values[element.toggle_field] !== element.toggle_value) {
            return;
          }
          if (element.type === 'rect') {
            target.add(new fabric.Rect({
              left: element['x-pos'],
              top: element['y-pos'],
              width: element.width,
              height: element.height,
              fill: element.fill || '#000000',
            }));
          } else if (element.type === 'text') {
            const text = this.textFor(element, draft);
            if (text) target.add(this.buildText(element, text, draft));
          }
        });
      },
      async paintText() {
        if (!canvas) return;
        const version = ++textVersion;
        const draft = this.draft;
        const portraitId = placedPortraitId;
        if (draft && portraitId) await ensureFont(draft.font);
        if (version !== textVersion || !canvas) return;
        textObjects.forEach((object) => canvas.remove(object));
        textObjects.length = 0;
        if (draft && portraitId && placedPortraitId === portraitId) {
          const before = new Set(canvas.getObjects());
          this.addText(canvas);
          canvas.getObjects().forEach((object) => {
            if (!before.has(object)) textObjects.push(object);
          });
        }
        canvas.renderAll();
      },
      textFor(element, draft) {
        let text = draft.values[element.id] || '';
        if (element.editable_type === 'date') {
          text = formatDate(text, draft.dateFormat);
        }
        text = String(text).trim();
        return element.uppercase ? text.toUpperCase() : text;
      },
      buildText(element, text, draft) {
        const object = new fabric.Text(text, {
          fontFamily: draft.font,
          fontWeight: element['font-weight'] || 'normal',
          fontStyle: element['font-style'] || 'normal',
          fontSize: element['font-size'] || 40,
          fill: draft.color || element['text-color'] || '#ffffff',
          textAlign: element['text-align'] || 'center',
          originX: element.originX || 'center',
          originY: element.originY || 'center',
          left: element['x-pos'],
          top: element['y-pos'],
          lineHeight: 1.1,
          shadow: new fabric.Shadow({ color: 'rgba(0, 0, 0, 0.6)', blur: 12, offsetX: 0, offsetY: 2 }),
        });

        const maxWidth = element.max_text_width;
        if (maxWidth && object.width > maxWidth) {
          const minSize = element.min_font_size || object.fontSize;
          object.set('fontSize', Math.max(minSize, Math.floor((object.fontSize * maxWidth) / object.width)));
          object.initDimensions();
          if (object.width > maxWidth) {
            object.set('scaleX', maxWidth / object.width);
          }
        }
        return object;
      },
      async renderDesign({ textOnly = false } = {}) {
        if (!canvas) return;
        if (textOnly && placedPortraitId && this.selectedImage?.id === placedPortraitId) {
          await this.paintText();
          return;
        }

        const version = ++renderVersion;
        const draft = this.draft;
        let image = this.selectedImage;
        const previewCached = Boolean(
          image && previewImages.has(image.id) && portraitUrls.get(image.id) === image.url,
        );

        if (image && !previewCached) {
          await this.refreshIfStale();
          if (version !== renderVersion) return;
          image = this.selectedImage;
        }

        if (!image) {
          this.clearPreview();
          canvas.renderAll();
          return;
        }

        if (!previewCached) this.canvasLoader = true;
        let portrait = null;
        try {
          portrait = await this.previewImageFor(image);
          this.refetchedForImages = false;
          if (version === renderVersion && this.portraitFailed) {
            this.portraitFailed = false;
            this.statusMessage = '';
            this.statusIsError = false;
          }
        } catch (error) {
          if (version !== renderVersion) return;
          if (!this.refetchedForImages) {
            this.refetchedForImages = true;
            await this.loadOrder();
            return;
          }
          this.portraitFailed = true;
          this.statusMessage = 'We couldn’t load this portrait. Refresh the page to try again.';
          this.statusIsError = true;
        } finally {
          if (version === renderVersion) this.canvasLoader = false;
        }

        if (version !== renderVersion) return;

        if (!portrait) {
          this.clearPreview();
          canvas.renderAll();
          return;
        }

        if (previewPortrait !== portrait) {
          if (previewPortrait) canvas.remove(previewPortrait);
          coverImage(portrait, canvas);
          canvas.sendToBack(portrait);
          previewPortrait = portrait;
          placedPortraitId = image.id;
        }

        const overlayKey = this.hasCalendar(image) ? (this.calendarFullBleed ? 'full' : 'inset') : '';
        if (overlayKey !== placedOverlayKey) {
          if (previewOverlay) {
            canvas.remove(previewOverlay);
            previewOverlay = null;
          }
          if (overlayKey) {
            try {
              const overlayElement = await loadOverlayElement('edge');
              if (version !== renderVersion) return;
              previewOverlay = new fabric.Image(overlayElement, { objectCaching: true });
              placeOverlay(previewOverlay, canvas, this.calendarFullBleed);
              this.overlayFailed = false;
            } catch (error) {
              if (version !== renderVersion) return;
              this.overlayFailed = true;
              this.statusMessage = 'We couldn’t load the calendar overlay. Refresh the page to try again.';
              this.statusIsError = true;
            }
          } else if (this.overlayFailed) {
            this.overlayFailed = false;
            if (this.statusIsError && this.statusMessage.startsWith('We couldn’t load the calendar overlay')) {
              this.statusMessage = '';
              this.statusIsError = false;
            }
          }
          placedOverlayKey = overlayKey;
        }

        if (version !== renderVersion) return;
        await this.paintText();
        if (placedPortraitId === image.id) this.queueThumbs();
      },
      async exportPrint(image = this.selectedImage) {
        const draft = this.draft;
        if (!image) {
          throw new Error('We couldn’t load this portrait. Refresh the page to try again.');
        }
        const portraitSource = await this.portraitElement(image, EXPORT_WIDTH);
        const element = document.createElement('canvas');
        const exportCanvas = new fabric.StaticCanvas(element, {
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          backgroundColor: '#ffffff',
          enableRetinaScaling: false,
          renderOnAddRemove: false,
        });
        try {
          const portrait = new fabric.Image(portraitSource, { objectCaching: false });
          coverImage(portrait, exportCanvas);
          if (this.hasCalendar(image)) {
            const overlaySource = await loadOverlayElement('edge');
            const overlay = new fabric.Image(overlaySource, { objectCaching: false });
            placeOverlay(overlay, exportCanvas, this.calendarFullBleed);
          }
          if (draft) await ensureFont(draft.font);
          this.addText(exportCanvas);
          exportCanvas.renderAll();
          return exportCanvas.toDataURL({ format: 'png', multiplier: 2 });
        } finally {
          exportCanvas.dispose();
        }
      },
      validate() {
        const draft = this.draft;
        this.errorFields = elements
          .filter((element) => element.editable && element.required)
          .filter((element) => !String(draft.values[element.id] || '').trim())
          .map((element) => element.id);
        return this.errorFields.length === 0;
      },
      prepareDownloads(images) {
        if (!this.saveToPhotos) {
          return;
        }
        (images || []).forEach((image) => {
          if (image?.url) {
            cachedFetchImage(image.url);
          }
        });
      },
      async portraitDownloadBlob(image) {
        const portraitSource = await this.portraitElement(image, EXPORT_WIDTH);
        const width = CANVAS_WIDTH * 2;
        const height = CANVAS_HEIGHT * 2;
        const element = document.createElement('canvas');
        element.width = width;
        element.height = height;
        const context = element.getContext('2d');
        const scale = Math.max(width / portraitSource.width, height / portraitSource.height);
        const drawnWidth = portraitSource.width * scale;
        const drawnHeight = portraitSource.height * scale;
        context.drawImage(
          portraitSource,
          (width - drawnWidth) / 2,
          (height - drawnHeight) / 2,
          drawnWidth,
          drawnHeight,
        );
        if (this.hasCalendar(image)) {
          const overlay = await loadOverlayElement('edge');
          const inset = this.calendarFullBleed ? 0 : 0.03;
          context.drawImage(
            overlay,
            width * inset,
            height * inset,
            width * (1 - inset * 2),
            height * (1 - inset * 2),
          );
        }
        const blob = await new Promise((resolve, reject) => {
          element.toBlob((result) => {
            if (result) resolve(result);
            else reject(new Error('We couldn’t save this portrait.'));
          }, 'image/png');
        });
        return blob;
      },
      async openPhotoSheet(images) {
        const photos = [];
        for (const [index, image] of images.entries()) {
          let url = image.url;
          let revoke = false;
          const canComposite = this.hasCalendar(image) && url && !String(url).startsWith('data:') && !String(url).startsWith('blob:');
          if (canComposite) {
            const blob = await this.portraitDownloadBlob(image);
            url = URL.createObjectURL(blob);
            revoke = true;
          }
          photos.push({
            id: image.id,
            url,
            revoke,
            label: image.label || `Portrait ${this.portraitNumber(image) || index + 1}`,
          });
        }
        this.photoSheet = photos;
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => this.$refs.photoSheetClose?.focus());
      },
      closePhotoSheet() {
        if (!this.photoSheet.length) {
          return;
        }
        this.photoSheet.forEach((photo) => {
          if (photo.revoke && photo.url) URL.revokeObjectURL(photo.url);
        });
        this.photoSheet = [];
        document.body.style.overflow = '';
      },
      async imageFile(image) {
        const number = this.portraitNumber(image);
        const orderRef = this.orderNumber || this.orderId;
        const prefix = orderRef ? `order-${String(orderRef).replace(/[^\w-]/g, '')}` : 'firefighter';
        if (this.hasCalendar(image)) {
          const blob = await this.portraitDownloadBlob(image);
          const name = `${prefix}-print-${this.activeIndex + 1}-portrait-${number}-calendar.png`;
          return new File([blob], name, { type: 'image/png' });
        }
        const blob = await cachedFetchImage(image.url);
        const type = await imageMimeType(blob);
        const name = `${prefix}-print-${this.activeIndex + 1}-portrait-${number}.${extensionForType(type)}`;
        return new File([blob], name, { type });
      },
      async saveImagesToPhotos(images) {
        if (!images.length) {
          return;
        }
        this.downloadProgress = images.length > 1 ? 'Preparing photos…' : 'Preparing photo…';
        try {
          const files = await Promise.all(images.map((image) => this.imageFile(image)));
          const shared = await shareImageFiles(files);
          if (!shared) {
            await this.openPhotoSheet(images);
          }
        } catch (error) {
          if (images.some((image) => this.hasCalendar(image))) {
            this.statusMessage = error.message || 'We couldn’t save these portraits.';
            this.statusIsError = true;
            return;
          }
          await this.openPhotoSheet(images);
        }
      },
      async designedBlob(image) {
        const dataUrl = await this.exportPrint(image);
        return (await fetch(dataUrl)).blob();
      },
      async downloadDesignedImages(images) {
        if (this.downloading || !images.length) {
          return;
        }
        this.downloading = true;
        const ids = images.map((image) => image.id);
        try {
          await this.refreshIfStale();
          const fresh = ids.map((id) => this.activeImages.find((image) => image.id === id)).filter(Boolean);
          if (fresh.some((image) => this.hasCalendar(image)) && this.overlayFailed) {
            throw new Error('We couldn’t load the calendar overlay. Refresh the page to try again.');
          }
          const orderRef = this.orderNumber || this.orderId;
          const prefix = orderRef ? `order-${String(orderRef).replace(/[^\w-]/g, '')}` : 'firefighter';
          if (this.saveToPhotos) {
            this.downloadProgress = fresh.length > 1 ? 'Preparing photos…' : 'Preparing photo…';
            const files = [];
            for (const image of fresh) {
              const blob = await this.designedBlob(image);
              const number = this.portraitNumber(image);
              const calendar = this.hasCalendar(image) ? '-calendar' : '';
              files.push(new File([blob], `${prefix}-portrait-${number}${calendar}.png`, { type: 'image/png' }));
            }
            const shared = await shareImageFiles(files);
            if (!shared) {
              const photos = files.map((file, index) => ({
                id: fresh[index].id,
                url: URL.createObjectURL(file),
                revoke: true,
                label: fresh[index].label || `Portrait ${this.portraitNumber(fresh[index])}`,
              }));
              this.photoSheet = photos;
              document.body.style.overflow = 'hidden';
              this.$nextTick(() => this.$refs.photoSheetClose?.focus());
            }
            return;
          }
          for (const [position, image] of fresh.entries()) {
            this.downloadProgress = fresh.length > 1 ? `Downloading ${position + 1} of ${fresh.length}…` : 'Downloading…';
            const blob = await this.designedBlob(image);
            const number = this.portraitNumber(image);
            const calendar = this.hasCalendar(image) ? '-calendar' : '';
            const objectUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = objectUrl;
            link.download = `${prefix}-portrait-${number}${calendar}.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
            await wait(350);
          }
        } catch (error) {
          this.statusMessage = error.message || 'We couldn’t download these images.';
          this.statusIsError = true;
        } finally {
          this.downloading = false;
          this.downloadProgress = '';
        }
      },
      async downloadImages(images) {
        if (this.isDigital) {
          await this.downloadDesignedImages(images);
          return;
        }
        if (this.downloading || !images.length) {
          return;
        }
        this.downloading = true;
        const ids = images.map((image) => image.id);
        try {
          await this.refreshIfStale();
          const fresh = ids.map((id) => this.activeImages.find((image) => image.id === id)).filter(Boolean);
          if (fresh.some((image) => this.hasCalendar(image)) && this.overlayFailed) {
            throw new Error('We couldn’t load the calendar overlay. Refresh the page to try again.');
          }
          if (this.saveToPhotos) {
            await this.saveImagesToPhotos(fresh);
            return;
          }
          const orderRef = this.orderNumber || this.orderId;
          const prefix = orderRef ? `order-${String(orderRef).replace(/[^\w-]/g, '')}` : 'firefighter';
          for (const [position, image] of fresh.entries()) {
            this.downloadProgress = fresh.length > 1 ? `Downloading ${position + 1} of ${fresh.length}…` : 'Downloading…';
            const number = this.portraitNumber(image);
            const link = document.createElement('a');
            let objectUrl = '';
            const withCalendar = this.hasCalendar(image);
            try {
              const blob = withCalendar ? await this.portraitDownloadBlob(image) : await fetchImage(image.url);
              const extension = withCalendar
                ? 'png'
                : (blob.type.split('/')[1] || 'png').split('+')[0].replace('jpeg', 'jpg');
              objectUrl = URL.createObjectURL(blob);
              link.href = objectUrl;
              link.download = `${prefix}-print-${this.activeIndex + 1}-portrait-${number}${withCalendar ? '-calendar' : ''}.${extension}`;
            } catch (error) {
              if (withCalendar) throw error;
              link.href = image.url;
              link.target = '_blank';
              link.rel = 'noopener';
            }
            document.body.appendChild(link);
            link.click();
            link.remove();
            if (objectUrl) {
              setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
            }
            await wait(350);
          }
        } catch (error) {
          this.statusMessage = error.message || 'We couldn’t download these portraits.';
          this.statusIsError = true;
        } finally {
          this.downloading = false;
          this.downloadProgress = '';
        }
      },
      async downloadPreview() {
        if (this.downloading || this.canvasLoader || this.portraitFailed || !this.selectedImage) {
          return;
        }
        this.downloading = true;
        this.downloadProgress = this.saveToPhotos ? 'Preparing…' : 'Downloading…';
        try {
          if (this.calendarOn && this.overlayFailed) {
            throw new Error('We couldn’t load the calendar overlay. Refresh the page to try again.');
          }
          const dataUrl = await this.exportPrint();
          const blob = await (await fetch(dataUrl)).blob();
          const number = this.portraitNumber(this.selectedImage);
          const orderRef = this.orderNumber || this.orderId;
          const prefix = orderRef ? `order-${String(orderRef).replace(/[^\w-]/g, '')}` : 'firefighter';
          const calendar = this.calendarOn ? '-calendar' : '';
          const name = `${prefix}-preview-${number}${calendar}.png`;
          const file = new File([blob], name, { type: 'image/png' });
          if (this.saveToPhotos) {
            const shared = await shareImageFiles([file]);
            if (!shared) {
              this.openPhotoSheet([{
                id: this.selectedImage.id,
                url: dataUrl,
                label: 'Preview',
              }]);
            }
            return;
          }
          const objectUrl = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = objectUrl;
          link.download = name;
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
        } catch (error) {
          this.statusMessage = error.message || 'We couldn’t save this preview.';
          this.statusIsError = true;
        } finally {
          this.downloading = false;
          this.downloadProgress = '';
        }
      },
      portraitNumber(image) {
        const index = this.activeImages.findIndex((candidate) => candidate.id === image?.id);
        return index >= 0 ? index + 1 : 1;
      },
      schedulePoll() {
        clearTimeout(this.pollTimer);
        const stillWorking = this.hasRegeneratingTemplates || this.items.some((item) => {
          const status = item.run?.status;
          const images = Array.isArray(item.run?.images) ? item.run.images : [];
          const unfinished = Boolean(status) && !['complete', 'failed'].includes(status);
          return unfinished || (this.shouldPoll && !images.length);
        });
        if (stillWorking) {
          this.pollTimer = setTimeout(() => this.loadOrder(), this.pollIntervalMs);
        }
      },
      isTemplateBusy(image) {
        if (image?.generation_template_id == null) return false;
        return this.regeneratingIds.includes(String(image.generation_template_id));
      },
      isTemplateFailed(image) {
        if (image?.generation_template_id == null) return false;
        return this.failedIds.includes(String(image.generation_template_id));
      },
      openRegenFlow() {
        if (this.regenLocked) return;
        this.regenError = '';
        this.regenNotice = '';
        this.regenStep = 'feedback';
        this.regenOpen = true;
        document.body.style.overflow = 'hidden';
        this.$nextTick(() => this.$refs.regenClose?.focus());
      },
      startPhotoGuide() {
        this.regenError = '';
        this.regenStep = 'guide';
      },
      feedbackIcon(code) {
        return feedbackIconSvg(code);
      },
      closeRegen(force = false) {
        if (this.regenBusy && !force) return;
        this.regenOpen = false;
        this.regenError = '';
        if (!this.photoSheet.length) document.body.style.overflow = '';
      },
      mountPhotoGuide() {
        const host = this.$refs.photoGuideHost;
        const source = document.getElementById('afp-photo-guide-panels');
        if (!host || !source?.content || host.childElementCount) return;
        host.appendChild(source.content.cloneNode(true));
      },
      async sessionFetch(path, options = {}) {
        const headers = { Accept: 'application/json', ...(options.headers || {}) };
        if (storeKey) headers['X-Store-Key'] = storeKey;
        return this.jsonFetch(withKey(path), { ...options, headers });
      },
      async loadRegenSession() {
        const id = this.activeRun?.upload_session_id;
        if (!id) throw new Error('Photo upload isn’t available for this print yet.');
        const data = await this.sessionFetch('/api/generation-sessions/' + id);
        const payload = unwrapRegenPayload(data) || {};
        const existing = Array.isArray(payload.photos) ? payload.photos : [];
        this.clearRegenPhotos();
        this.regenSessionId = payload.id || id;
        this.regenPriorIds = existing.map((photo) => photo?.id).filter((photoId) => photoId != null);
        this.regenExistingCount = 0;
        this.regenBlockingReason = '';
        this.regenReadyForCheckout = false;
        this.regenPhotos = [];
      },
      async openRegenUpload() {
        if (this.regenBusy) return;
        this.regenError = '';
        this.regenBusy = true;
        try {
          await this.loadRegenSession();
          await this.removePriorRegenPhotos();
          this.regenStep = 'upload';
        } catch (error) {
          this.regenError = error.message;
        } finally {
          this.regenBusy = false;
        }
      },
      openRegenPicker() {
        this.$refs.regenFiles?.click();
      },
      onRegenFiles(event) {
        this.addRegenFiles(event.target.files);
        event.target.value = '';
      },
      onRegenDrop(event) {
        this.addRegenFiles(event.dataTransfer?.files);
      },
      async addRegenFiles(list) {
        const incoming = [...(list || [])];
        if (!incoming.length) return;
        this.regenError = '';
        try {
          await this.removePriorRegenPhotos();
        } catch (error) {
          this.regenError = error.message || 'Something went wrong. Please try again.';
          return;
        }
        let rejected = '';
        incoming.forEach((file) => {
          if (this.regenPhotos.length >= MAX_REGEN_PHOTOS) {
            rejected = `You can upload up to ${MAX_REGEN_PHOTOS} photos.`;
            return;
          }
          if (!isAllowedRegenFile(file)) {
            rejected = 'Use JPG or PNG photos.';
            return;
          }
          if (file.size > MAX_REGEN_BYTES) {
            rejected = `${file.name} is over 10MB.`;
            return;
          }
          this.regenPhotos.push({
            localId: regenLocalId,
            file,
            previewUrl: URL.createObjectURL(file),
            previewIsBlob: true,
            id: null,
            verdict: '',
            status: '',
            message: '',
            role: '',
            uploading: false,
            deleting: false,
          });
          regenLocalId += 1;
        });
        this.regenError = rejected;
        this.uploadPendingRegenPhotos();
      },
      applyRegenSessionFlags(data) {
        const payload = data && typeof data === 'object' ? data : {};
        if (payload.ready_for_checkout !== undefined) this.regenReadyForCheckout = payload.ready_for_checkout === true;
        if (payload.ready_for_checkout === true) this.regenBlockingReason = '';
        else if (payload.blocking_reason !== undefined) this.regenBlockingReason = payload.blocking_reason || '';
        const stored = this.regenPhotos.filter((photo) => photo.id && !photo.uploading && !photo.deleting);
        const meetsQuality = stored.filter(isUsableFace).length >= 2
          && stored.some(isUsableFullBody)
          && !stored.some(isHardReject);
        if (payload.ready_for_checkout === undefined && !meetsQuality) this.regenReadyForCheckout = false;
      },
      applyRegenPhoto(photo, remote) {
        const data = unwrapRegenPayload(remote) || {};
        const nested = data.photo && typeof data.photo === 'object' ? data.photo : null;
        const barePhoto = !nested && !Array.isArray(data.photos) && (data.verdict || data.status || data.role || data.message);
        if (nested) copyRegenPhotoFields(photo, nested);
        else if (barePhoto) copyRegenPhotoFields(photo, data);
        if (Array.isArray(data.photos)) {
          const match = photo.id != null
            ? data.photos.find((item) => sameRegenPhotoId(item?.id, photo.id))
            : null;
          if (match) copyRegenPhotoFields(photo, match);
          else if (photo.id == null) {
            const taken = new Set(
              this.regenPhotos
                .filter((item) => item !== photo && item.id != null)
                .map((item) => String(item.id)),
            );
            const fresh = [...data.photos].reverse().find((item) => item?.id != null && !taken.has(String(item.id)));
            if (fresh) copyRegenPhotoFields(photo, fresh);
          }
        }
        photo.uploading = false;
        if (photo.id == null || photo.id === '') {
          photo.message = photo.message || 'That photo could not be uploaded. Please try again.';
        }
        this.applyRegenSessionFlags(data);
      },
      async removePriorRegenPhotos() {
        if (!this.regenPriorIds.length) return;
        if (this.regenReplaceFlight) return this.regenReplaceFlight;
        const sessionId = this.regenSessionId || this.activeRun?.upload_session_id;
        if (!sessionId) throw new Error('Photo upload isn’t available for this print yet.');
        const ids = this.regenPriorIds.slice();
        this.regenReplaceFlight = (async () => {
          for (const photoId of ids) {
            try {
              await this.sessionFetch(`/api/generation-sessions/${sessionId}/photos/${photoId}`, {
                method: 'DELETE',
              });
            } catch (error) {
              if (error.status !== 404) throw error;
            }
          }
          this.regenPriorIds = [];
          this.regenExistingCount = 0;
        })();
        try {
          await this.regenReplaceFlight;
        } finally {
          this.regenReplaceFlight = null;
        }
      },
      clearRegenPhotos() {
        this.regenPhotos.forEach((photo) => {
          if (photo.previewIsBlob && photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
        });
        this.regenPhotos = [];
      },
      async uploadRegenPhoto(photo, replace = false) {
        if (!photo.file || !isAllowedRegenFile(photo.file)) throw new Error('Use JPG or PNG photos.');
        if (photo.file.size > MAX_REGEN_BYTES) throw new Error(`${photo.file.name} is over 10MB.`);
        const id = this.regenSessionId || this.activeRun?.upload_session_id;
        if (!id) throw new Error('Photo upload isn’t available for this print yet.');
        photo.uploading = true;
        photo.message = '';
        const body = new FormData();
        body.append('image', photo.file);
        const path = replace && photo.id
          ? `/api/generation-sessions/${id}/photos/${photo.id}`
          : `/api/generation-sessions/${id}/photos`;
        const remote = await this.sessionFetch(path, { method: 'POST', body });
        this.applyRegenPhoto(photo, remote);
      },
      async uploadPendingRegenPhotos() {
        if (this.regenUploadQueue) return this.regenUploadQueue;
        this.regenUploadQueue = this.runRegenUploadQueue().finally(() => {
          this.regenUploadQueue = null;
        });
        return this.regenUploadQueue;
      },
      async runRegenUploadQueue() {
        for (const photo of this.regenPhotos) {
          if (photo.id || photo.uploading || photo.message) continue;
          photo.uploading = true;
          try {
            await this.uploadRegenPhoto(photo);
          } catch (error) {
            photo.uploading = false;
            photo.message = error.message;
            this.regenError = error.message;
          }
        }
      },
      photoDisplayVerdict(photo) {
        const quality = photoQuality(photo);
        if (quality === 'accepted') return 'good';
        if (quality === 'warning') return 'warning';
        if (quality === 'rejected') return 'rejected';
        return '';
      },
      photoNeedsReplace(photo) {
        return isHardReject(photo);
      },
      photoMessage(photo) {
        if (photoQuality(photo) === 'accepted') return '';
        const feedback = String(photo.message || '').trim();
        const body = /^great photo\.?$/i.test(feedback) ? '' : feedback;
        if (!body) return '';
        if (isGroupMessage(body)) return body;
        if (isOnePersonMessage(body)) return EXTRA_PERSON_COPY;
        return body;
      },
      pickRegenReplace(index) {
        this.regenReplaceIndex = index;
        const input = this.$refs.regenReplace;
        if (!input) return;
        input.value = '';
        input.click();
      },
      async onRegenReplace(event) {
        const file = event.target.files?.[0];
        const photo = this.regenPhotos[this.regenReplaceIndex];
        event.target.value = '';
        this.regenReplaceIndex = -1;
        if (!file || !photo || photo.uploading || photo.deleting) return;
        if (!isAllowedRegenFile(file)) {
          this.regenError = 'Use JPG or PNG photos.';
          return;
        }
        if (file.size > MAX_REGEN_BYTES) {
          this.regenError = `${file.name} is over 10MB.`;
          return;
        }
        if (photo.previewIsBlob && photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
        photo.file = file;
        photo.previewUrl = URL.createObjectURL(file);
        photo.previewIsBlob = true;
        photo.verdict = '';
        photo.status = '';
        photo.message = '';
        photo.role = '';
        this.regenError = '';
        try {
          await this.uploadRegenPhoto(photo, Boolean(photo.id));
        } catch (error) {
          photo.uploading = false;
          photo.message = error.message;
          this.regenError = error.message;
        }
      },
      async removeRegenPhoto(index) {
        const photo = this.regenPhotos[index];
        if (!photo || photo.uploading || photo.deleting) return;
        if (!photo.id || !this.regenSessionId) {
          if (photo.previewIsBlob && photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
          this.regenPhotos.splice(index, 1);
          this.applyRegenSessionFlags(null);
          return;
        }
        photo.deleting = true;
        try {
          const data = await this.sessionFetch(`/api/generation-sessions/${this.regenSessionId}/photos/${photo.id}`, {
            method: 'DELETE',
          });
          if (photo.previewIsBlob && photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
          this.regenPhotos.splice(index, 1);
          this.applyRegenSessionFlags(unwrapRegenPayload(data));
        } catch (error) {
          if (error.status === 404) {
            if (photo.previewIsBlob && photo.previewUrl) URL.revokeObjectURL(photo.previewUrl);
            this.regenPhotos.splice(index, 1);
            this.applyRegenSessionFlags(null);
            return;
          }
          photo.deleting = false;
          this.regenError = error.message;
        }
      },
      handleRegenError(error, { stayOnUpload = false } = {}) {
        let message = error.message || 'Something went wrong. Please try again.';
        if (error.status === 429) message = 'Please wait a moment and try again.';
        if (error.status === 404) message = 'This run isn’t part of this order.';
        if (error.status === 422 && /replaced by newer ones|no regenerations are left/i.test(message)) {
          this.loadOrder();
        }
        if (stayOnUpload) this.regenError = message;
        else this.regenNotice = message;
      },
      async regenerate({ imageId, stayOnUpload = false, holdingLock = false } = {}) {
        const run = this.activeRun;
        if (!run?.gallery_token) return;
        if (this.regenBusy && !holdingLock) return;
        this.regenBusy = true;
        this.regenError = '';
        this.regenNotice = '';
        try {
          const body = { run_gallery_token: run.gallery_token };
          if (imageId) body.image_id = imageId;
          const payload = await this.sessionFetch('/api/generation-orders/' + token + '/regenerate', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
          });
          if (!imageId) this.clearRegenPhotos();
          this.closeRegen(true);
          this.applyOrder(payload);
          this.schedulePoll();
        } catch (error) {
          this.handleRegenError(error, { stayOnUpload });
        } finally {
          if (!holdingLock) this.regenBusy = false;
        }
      },
      regenerateScene() {
        this.openRegenFlow();
      },
      regenerateAll() {
        this.openRegenFlow();
      },
      async finishPhotoUpload() {
        if (!this.regenReady || this.regenBusy) return;
        this.regenBusy = true;
        this.regenError = '';
        try {
          await this.removePriorRegenPhotos();
          await this.uploadPendingRegenPhotos();
          const blocked = Boolean(this.regenPhotoMessage)
            || this.regenPhotos.some((photo) => !photo.id || photo.uploading || photo.deleting);
          if (blocked) return;
          await this.regenerate({ stayOnUpload: true, holdingLock: true });
        } catch (error) {
          this.regenError = error.message || 'Something went wrong. Please try again.';
        } finally {
          this.regenBusy = false;
        }
      },
      async confirmSelection() {
        const item = this.activeItem;
        const image = this.selectedImage;
        if (!item || !image || this.saving) {
          return;
        }
        if (!this.validate()) {
          this.slideTray(true);
          this.statusMessage = 'Fill in the highlighted fields first.';
          this.statusIsError = true;
          return;
        }

        this.saving = true;
        this.statusIsError = false;
        this.statusMessage = this.isDigital ? 'Saving your text…' : 'Uploading your print…';
        const savedIndex = this.activeIndex;

        try {
          if (this.previewDigital) {
            item.run.selected_image_id = image.id;
            this.allSelected = !this.items.some(isOpen);
            this.statusMessage = '';
            return;
          }
          clearTimeout(textRenderTimer);
          renderMode = null;
          await this.renderDesign({ textOnly: false });
          if (this.portraitFailed || placedPortraitId !== image.id) {
            throw new Error('We couldn’t load this portrait. Refresh the page to try again.');
          }
          if (this.hasCalendar(image) && this.overlayFailed) {
            throw new Error('We couldn’t load the calendar overlay. Refresh the page to try again.');
          }
          const printImage = await this.exportPrint();
          const uploaded = await this.jsonFetch(withKey('/api/upload_image'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              image: printImage,
              user_id: 'generation-selection',
            }),
          });

          if (!uploaded.file_url) {
            throw new Error('Upload succeeded but no file URL was returned.');
          }

          const payload = await this.jsonFetch(withKey('/api/generation-orders/' + token + '/select'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              run_gallery_token: item.run.gallery_token,
              image_id: image.id,
              new_design_url: uploaded.file_url,
              edge_to_edge: Boolean(this.draft?.edgeToEdge),
            }),
          });

          this.applyOrder(payload);
          this.schedulePoll();
          if (this.allSelected) {
            return;
          }

          const next = this.nextOpenIndex(savedIndex + 1, savedIndex);
          if (next !== savedIndex) {
            const savedNoun = isDigitalItem(this.items[savedIndex]) ? 'design' : 'print';
            const nextNoun = isDigitalItem(this.items[next]) ? 'design' : 'print';
            this.goTo(next);
            const label = (noun) => `${noun[0].toUpperCase()}${noun.slice(1)}`;
            this.statusMessage = `${label(savedNoun)} ${savedIndex + 1} saved. Now customise ${nextNoun} ${next + 1}.`;
          } else {
            this.statusMessage = 'Saved.';
          }
          this.statusIsError = false;
          window.scrollTo({ top: root.getBoundingClientRect().top + window.scrollY - 20, behavior: 'smooth' });
        } catch (error) {
          if (error.status === 422 && /replaced by newer ones/i.test(error.message || '')) {
            this.loadOrder();
          }
          this.statusMessage = error.message;
          this.statusIsError = true;
        } finally {
          this.saving = false;
        }
      },
      formatExtraMoney(cents) {
        return formatMoney(Number(cents) || 0, extraCatalog.money_format);
      },
      showExtraOffer() {
        this.extraHidden = false;
        this.extraNotice = '';
        this.extraError = '';
      },
      dismissExtra() {
        this.extraHidden = true;
      },
      pickExtra(choice) {
        this.extraChoiceKey = choice.key;
        this.extraNotice = '';
        this.extraError = '';
      },
      pickExtraSize(key) {
        this.extraSizeKey = key;
        this.extraNotice = '';
        this.extraError = '';
      },
      async buyExtra() {
        const choice = this.extraChoice;
        if (!choice || this.extraBuying) return;
        this.extraError = '';
        this.extraNotice = '';
        if (this.previewUpsell) {
          this.extraNotice = 'This is a preview. No payment was taken. On a live order, checkout opens for this extra print at 15% off and links it to the original order.';
          return;
        }
        if (!this.extraSize?.id) {
          this.extraError = 'This print size isn’t linked in Theme settings.';
          return;
        }
        this.extraBuying = true;
        try {
          const payload = await this.jsonFetch(withKey('/api/generation-orders/' + token + '/extra-print'), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              image_id: choice.imageId,
              same_design: false,
              size: this.selectedExtraSize,
              stand: this.extraStand ? choice.stand : 'none',
              variant_id: this.extraSize.id,
              stand_variant_id: this.extraStand?.id || null,
              discount_percent: 15,
              print_price_cents: this.extraPrintPrice,
              sale_price_cents: this.extraSalePrice,
              stand_price_cents: this.extraStandPrice,
              run_gallery_token: choice.galleryToken,
              properties: {
                _upsell: 'extra-print',
                _source_order_token: token,
                _source_order_id: this.orderId == null ? '' : String(this.orderId),
                _image_id: String(choice.imageId),
                _same_design: 'false',
              },
            }),
          });
          if (!payload.checkout_url) {
            throw new Error('Checkout for this extra print is not ready yet. Your saved print is unchanged.');
          }
          window.location.assign(payload.checkout_url);
        } catch (error) {
          this.extraError = error.status === 404
            ? 'Checkout for this extra print is not ready yet. Your saved print is unchanged.'
            : (error.message || 'Could not open checkout.');
          this.extraBuying = false;
        }
      },
    },
  }).mount('#app');
})();
